import React from "react";

import mixedGallery from "../data/gallery-mixed.json";
import portraitGallery from "../data/gallery-portrait.json";
import landscapeGallery from "../data/gallery-landscape.json";

const galleryById = Object.fromEntries(
	mixedGallery.map((item) => [item.id, item])
);

const Row = ({ ids, onImageClick }) => (
	<div className="flex h-36 w-full gap-1.5 md:h-44 lg:h-52 xl:h-60 2xl:h-72">
		{ids.map((id) => {
			const item = galleryById[id];
			if (!item) return null;

			return (
				<button
					key={item.id}
					type="button"
					onClick={() => onImageClick(item.imageURL)}
					className="min-w-0 overflow-hidden"
					style={{
						flexGrow: item.type === "landscape" ? 1.7 : 0.75,
						flexShrink: 1,
						flexBasis: 0,
					}}
					aria-label={`Open gallery image ${item.id}`}
				>
					<img
						src={item.imageURL}
						alt={`Gallery ${item.id}`}
						className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
						loading="lazy"
					/>
				</button>
			);
		})}
	</div>
);

function Gallery() {
	const [highlightedImage, setHighlightedImage] = React.useState("");

	const handleImageClick = (imageURL) => {
		setHighlightedImage(imageURL);
	};

	const rows = [
		[1, 2, 3, 4, 5],
		[6, 7, 8, 9, 10],
		[11, 12, 13, 14, 15],
		[16, 17, 18, 19, 20, 21],
		[22, 23, 24, 25, 26, 27],
		[28, 29, 30],
	];

	return (
		<div>
			<section className="bg-white px-4 py-12 sm:px-6 md:px-8 md:py-16">
				<div className="mx-auto max-w-3xl text-center">
					<h1 className="font-inter text-[28px] font-bold leading-tight text-[#0f172a] sm:text-4xl md:text-[42px]">
						Welcome to our gallery,
					</h1>
					<p className="mt-5 text-sm leading-relaxed text-slate-500 sm:text-[15px] md:text-base">
						Discover how Mid-State Ag Supply LLC’s solutions make a
						difference in the field. Our gallery showcases
						real-world applications of our ventilation systems, barn
						curtains, and fans, highlighting their impact on farm
						productivity and animal comfort.
					</p>
					<p className="mt-4 text-[13px] leading-relaxed text-slate-400 sm:text-sm md:text-[15px]">
						See our expertly designed products in action, enhancing
						livestock environments and optimizing farming
						operations. Explore the practical benefits and
						exceptional quality of our solutions through these
						visual examples.
					</p>
					<div className="mx-auto mt-6 h-[3px] w-12 rounded-full bg-primary"></div>
				</div>

				<div className="mx-auto mt-10 hidden max-w-[1600px] space-y-1.5 md:mt-12 md:block">
					{rows.map((ids) => (
						<Row
							key={ids.join("-")}
							ids={ids}
							onImageClick={handleImageClick}
						/>
					))}
				</div>

				<div className="mx-auto mt-10 grid max-w-3xl grid-cols-2 gap-1.5 sm:grid-cols-3 md:hidden">
					{portraitGallery.map((item) => (
						<button
							key={item.id}
							type="button"
							onClick={() => handleImageClick(item.imageURL)}
							className="overflow-hidden"
							aria-label={`Open portrait gallery image ${item.id}`}
						>
							<img
								src={item.imageURL}
								alt={`Portrait ${item.id}`}
								className="aspect-[3/4] h-full w-full object-cover"
								loading="lazy"
							/>
						</button>
					))}
					{landscapeGallery.map((item) => (
						<button
							key={item.id}
							type="button"
							onClick={() => handleImageClick(item.imageURL)}
							className="col-span-2 overflow-hidden sm:col-span-3"
							aria-label={`Open landscape gallery image ${item.id}`}
						>
							<img
								src={item.imageURL}
								alt={`Landscape ${item.id}`}
								className="aspect-video w-full object-cover"
								loading="lazy"
							/>
						</button>
					))}
				</div>
			</section>

			{highlightedImage && (
				<div
					className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/80 p-3 sm:p-6"
					onClick={() => setHighlightedImage("")}
					role="dialog"
					aria-modal="true"
					aria-label="Fullscreen gallery image"
				>
					<img
						src={highlightedImage}
						alt="Fullscreen"
						className="max-h-[85dvh] max-w-full object-contain sm:max-h-[90vh] sm:max-w-[90%]"
					/>
				</div>
			)}
		</div>
	);
}

export default Gallery;
